import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { frenchDateOnly, frenchMonth } from '../../../../variables/functions';
import { displayNum } from '../../../../variables/numbersFunctions';
import { loadEntete, loadLogos, drawEtatHeaderFooter, finalizeEtatPdf, NAVY } from '../../sinistre/etatsSinistresFunctions';

/**
 * Export data to Excel with formatting
 * @param {Array} data - Array of data objects
 * @param {String} fileName - Name of the file to export
 * @param {String} sheetName - Name of the worksheet
 * @param {Object} options - Additional options for formatting
 */
export const exportToExcel = (data, fileName = 'export', sheetName = 'Sheet1', options = {}) => {
    try {
        // Create workbook and worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(data);

        // Auto-size columns
        const colWidths = [];
        const keys = Object.keys(data[0] || {});

        keys.forEach((key, i) => {
            const maxLength = Math.max(
                key.length,
                ...data.map(row => String(row[key] || '').length)
            );
            colWidths[i] = { wch: Math.min(maxLength + 2, 50) };
        });

        ws['!cols'] = colWidths;

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, sheetName);

        // Write file
        XLSX.writeFile(wb, `${fileName}.xlsx`);

        return true;
    } catch (error) {
        console.error('Error exporting to Excel:', error);
        return false;
    }
};

/**
 * Export GUM production report to Excel with proper formatting
 */
export const exportGUMProductionToExcel = (jours, mois, bureau = null) => {
    try {
        // Calculate totals
        const totals = {
            totalMotoPuissance0: jours.reduce((sum, jour) => sum + jour.nombreMotoPuissance0, 0),
            totalMontantPuissance0: jours.reduce((sum, jour) => sum + jour.montantPuissance0, 0),
            totalMotoPuissance1: jours.reduce((sum, jour) => sum + jour.nombreMotoPuissance1, 0),
            totalMontantPuissance1: jours.reduce((sum, jour) => sum + jour.montantPuissance1, 0),
            totalMotoPuissance2: jours.reduce((sum, jour) => sum + jour.nombreMotoPuissance2, 0),
            totalMontantPuissance2: jours.reduce((sum, jour) => sum + jour.montantPuissance2, 0),
            totalMotoPuissance3: jours.reduce((sum, jour) => sum + jour.nombreMotoPuissance3, 0),
            totalMontantPuissance3: jours.reduce((sum, jour) => sum + jour.montantPuissance3, 0),
            totalMotoTotal: jours.reduce((sum, jour) => sum + jour.nombreMotoTotal, 0),
            totalPrimeNette: jours.reduce((sum, jour) => sum + jour.primeNette, 0),
            totalTaxes: jours.reduce((sum, jour) => sum + jour.taxes, 0),
            totalPrimeTotale: jours.reduce((sum, jour) => sum + jour.primeTotale, 0),
            totalTimbre: jours.reduce((sum, jour) => sum + jour.timbre, 0),
            totalMontantVerse: jours.reduce((sum, jour) => sum + jour.montantVerse, 0),
            totalResteAReverser: jours.reduce((sum, jour) => sum + jour.primeTotale, 0),
        };

        // Prepare data for Excel
        const excelData = jours.map(jour => ({
            'DATE': frenchDateOnly(jour.date),
            'BUREAUX': jour.bureau,
            'PUISSANCE 0 - NOMBRE': jour.nombreMotoPuissance0,
            'PUISSANCE 0 - MONTANT': jour.montantPuissance0,
            'PUISSANCE 1 - NOMBRE': jour.nombreMotoPuissance1,
            'PUISSANCE 1 - MONTANT': jour.montantPuissance1,
            'PUISSANCE 2 - NOMBRE': jour.nombreMotoPuissance2,
            'PUISSANCE 2 - MONTANT': jour.montantPuissance2,
            'PUISSANCE 3 - NOMBRE': jour.nombreMotoPuissance3,
            'PUISSANCE 3 - MONTANT': jour.montantPuissance3,
            'NBRE DE MOTOS': jour.nombreMotoTotal,
            'PRIME NETTE': jour.primeNette,
            'TAXES': jour.taxes,
            'PRIME TTC': jour.primeTotale,
            'CUMUL': jour.cumul,
            'TIMBRE': jour.timbre,
            'MONTANT VERSE': jour.montantVerse,
            'RESTE A REVERSER': jour.montantRestant,
            'OBSERVATIONS': jour.observations || ''
        }));

        // Add totals row
        excelData.push({
            'DATE': 'TOTAL',
            'BUREAUX': '',
            'PUISSANCE 0 - NOMBRE': totals.totalMotoPuissance0,
            'PUISSANCE 0 - MONTANT': totals.totalMontantPuissance0,
            'PUISSANCE 1 - NOMBRE': totals.totalMotoPuissance1,
            'PUISSANCE 1 - MONTANT': totals.totalMontantPuissance1,
            'PUISSANCE 2 - NOMBRE': totals.totalMotoPuissance2,
            'PUISSANCE 2 - MONTANT': totals.totalMontantPuissance2,
            'PUISSANCE 3 - NOMBRE': totals.totalMotoPuissance3,
            'PUISSANCE 3 - MONTANT': totals.totalMontantPuissance3,
            'NBRE DE MOTOS': totals.totalMotoTotal,
            'PRIME NETTE': totals.totalPrimeNette,
            'TAXES': totals.totalTaxes,
            'PRIME TTC': totals.totalPrimeTotale,
            'CUMUL': '',
            'TIMBRE': totals.totalTimbre,
            'MONTANT VERSE': totals.totalMontantVerse,
            'RESTE A REVERSER': totals.totalResteAReverser,
            'OBSERVATIONS': ''
        });

        // Create workbook
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(excelData);

        // Set column widths
        ws['!cols'] = [
            { wch: 12 }, // DATE
            { wch: 20 }, // BUREAUX
            { wch: 10 }, // P0 NOMBRE
            { wch: 12 }, // P0 MONTANT
            { wch: 10 }, // P1 NOMBRE
            { wch: 12 }, // P1 MONTANT
            { wch: 10 }, // P2 NOMBRE
            { wch: 12 }, // P2 MONTANT
            { wch: 10 }, // P3 NOMBRE
            { wch: 12 }, // P3 MONTANT
            { wch: 12 }, // NBRE MOTOS
            { wch: 12 }, // PRIME NETTE
            { wch: 10 }, // TAXES
            { wch: 12 }, // PRIME TTC
            { wch: 12 }, // CUMUL
            { wch: 10 }, // TIMBRE
            { wch: 14 }, // MONTANT VERSE
            { wch: 14 }, // RESTE A REVERSER
            { wch: 20 }  // OBSERVATIONS
        ];

        XLSX.utils.book_append_sheet(wb, ws, 'Production GUM');

        const monthName = frenchMonth(mois.split('-')[1]);
        const year = mois.split('-')[0];
        const bureauName = bureau ? `_${bureau}` : '_National';
        const fileName = `Etat_Global_Production_GUM${bureauName}_${monthName}_${year}`;

        XLSX.writeFile(wb, `${fileName}.xlsx`);

        return true;
    } catch (error) {
        console.error('Error exporting GUM production to Excel:', error);
        return false;
    }
};

/**
 * Export Emissions Nettes to Excel
 */
export const exportEmissionsNettesToExcel = (emissions, categorie, exercice) => {
    try {
        const MONTH_COLS = [
            ['JANVIER', 'janvier'], ['FÉVRIER', 'fevrier'], ['MARS', 'mars'], ['AVRIL', 'avril'],
            ['MAI', 'mai'], ['JUIN', 'juin'], ['JUILLET', 'juillet'], ['AOÛT', 'aout'],
            ['SEPTEMBRE', 'septembre'], ['OCTOBRE', 'octobre'], ['NOVEMBRE', 'novembre'], ['DÉCEMBRE', 'decembre'],
        ];

        const rowOf = (e) => {
            const r = { 'COMPAGNIE': e.compagnie?.raisonSociale || e.compagnie?.code || '' };
            MONTH_COLS.forEach(([label, key]) => { r[label] = Number(e[key]) || 0; });
            r['TOTAL'] = Number(e.total) || 0;
            return r;
        };
        const excelData = emissions.map(rowOf);

        // Ligne TOTAL
        const totalRow = { 'COMPAGNIE': 'TOTAL' };
        MONTH_COLS.forEach(([label, key]) => {
            totalRow[label] = emissions.reduce((s, e) => s + (Number(e[key]) || 0), 0);
        });
        totalRow['TOTAL'] = emissions.reduce((s, e) => s + (Number(e.total) || 0), 0);
        excelData.push(totalRow);

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(excelData);

        // Largeurs : COMPAGNIE + 12 mois + TOTAL
        ws['!cols'] = [{ wch: 30 }, ...Array(13).fill({ wch: 14 })];

        XLSX.utils.book_append_sheet(wb, ws, 'Emissions Nettes');

        const fileName = `Emissions_Nettes_Cedees_${categorie}_${exercice}`;
        XLSX.writeFile(wb, `${fileName}.xlsx`);

        return true;
    } catch (error) {
        console.error('Error exporting emissions to Excel:', error);
        return false;
    }
};

/**
 * Export component to PDF using html2canvas
 * @param {React.RefObject} componentRef - Reference to the component to export
 * @param {String} fileName - Name of the PDF file
 * @param {Object} options - PDF options (orientation, format, etc.)
 */
export const exportToPDF = async (componentRef, fileName = 'export', options = {}) => {
    try {
        const {
            orientation = 'landscape',
            format = 'a4',
            quality = 0.95,
            scale = 2
        } = options;

        const element = componentRef.current;
        if (!element) {
            throw new Error('Component reference not found');
        }

        // Capture the component as canvas
        const canvas = await html2canvas(element, {
            scale: scale,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff'
        });

        const imgData = canvas.toDataURL('image/png', quality);

        // Create PDF
        const pdf = new jsPDF({
            orientation: orientation,
            unit: 'mm',
            format: format
        });

        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        // Add image to PDF
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

        // Save PDF
        pdf.save(`${fileName}.pdf`);

        return true;
    } catch (error) {
        console.error('Error exporting to PDF:', error);
        return false;
    }
};

/**
 * Export GUM Production to PDF using jsPDF AutoTable
 */
export const exportGUMProductionToPDF = async (jours, mois, bureau = null) => {
    try {
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const formattedNow = new Date().toLocaleString('fr-FR', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
        const [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]);

        const monthName = frenchMonth(mois.split('-')[1]);
        const year = mois.split('-')[0];

        const N = (v) => displayNum(v) || '0';
        const sum = (f) => jours.reduce((s, j) => s + (Number(j[f]) || 0), 0);
        const totals = {
            n0: sum('nombreMotoPuissance0'), m0: sum('montantPuissance0'),
            n1: sum('nombreMotoPuissance1'), m1: sum('montantPuissance1'),
            n2: sum('nombreMotoPuissance2'), m2: sum('montantPuissance2'),
            n3: sum('nombreMotoPuissance3'), m3: sum('montantPuissance3'),
            moto: sum('nombreMotoTotal'), pn: sum('primeNette'), tx: sum('taxes'),
            ttc: sum('primeTotale'), timbre: sum('timbre'), verse: sum('montantVerse'),
        };

        const body = jours.map((j) => ([
            frenchDateOnly(j.date),
            N(j.nombreMotoPuissance0), N(j.montantPuissance0),
            N(j.nombreMotoPuissance1), N(j.montantPuissance1),
            N(j.nombreMotoPuissance2), N(j.montantPuissance2),
            N(j.nombreMotoPuissance3), N(j.montantPuissance3),
            N(j.nombreMotoTotal), N(j.primeNette), N(j.taxes), N(j.primeTotale),
            N(j.timbre), N(j.montantVerse), N(j.montantRestant),
        ]));
        body.push([
            'TOTAL',
            N(totals.n0), N(totals.m0), N(totals.n1), N(totals.m1),
            N(totals.n2), N(totals.m2), N(totals.n3), N(totals.m3),
            N(totals.moto), N(totals.pn), N(totals.tx), N(totals.ttc),
            N(totals.timbre), N(totals.verse), N(totals.ttc),
        ]);
        const lastRow = body.length - 1;

        autoTable(doc, {
            head: [[
                'DATE',
                'P0\nNbre', 'P0\nMont', 'P1\nNbre', 'P1\nMont',
                'P2\nNbre', 'P2\nMont', 'P3\nNbre', 'P3\nMont',
                'Motos', 'P. Nette', 'Taxes', 'P. TTC', 'Timbre', 'Versé', 'Reste',
            ]],
            body,
            theme: 'grid',
            startY: 32,
            styles: {
                fontSize: 6.5,
                cellPadding: { top: 0.9, bottom: 0.9, left: 1.2, right: 1.2 },
                lineWidth: 0.1, lineColor: [215, 215, 225], textColor: [45, 45, 45],
                halign: 'right', valign: 'middle', overflow: 'linebreak',
            },
            headStyles: { fillColor: NAVY, textColor: 255, fontSize: 6.5, fontStyle: 'bold', halign: 'center', lineWidth: 0.1, lineColor: NAVY },
            alternateRowStyles: { fillColor: [246, 247, 251] },
            margin: { top: 32, bottom: 22, left: 12, right: 12 },
            columnStyles: {
                0: { cellWidth: 17, halign: 'center' },
                1: { cellWidth: 11 }, 2: { cellWidth: 17 },
                3: { cellWidth: 11 }, 4: { cellWidth: 17 },
                5: { cellWidth: 11 }, 6: { cellWidth: 17 },
                7: { cellWidth: 11 }, 8: { cellWidth: 17 },
                9: { cellWidth: 13 }, 10: { cellWidth: 18 }, 11: { cellWidth: 15 },
                12: { cellWidth: 18 }, 13: { cellWidth: 12 }, 14: { cellWidth: 14 }, 15: { cellWidth: 19 },
            },
            didParseCell: (d) => {
                if (d.row.index === lastRow) {
                    d.cell.styles.fontStyle = 'bold';
                    d.cell.styles.fillColor = [225, 227, 240];
                    d.cell.styles.textColor = NAVY;
                }
            },
            didDrawPage: () => drawEtatHeaderFooter(doc, {
                title: `ÉTAT DE PRODUCTION GUM — ${monthName.toUpperCase()} ${year}`,
                subtitle: bureau ? `Bureau : ${bureau}` : 'National (tous bureaux)',
                enteteData, logos, formattedNow,
            }),
        });

        const fileName = `Etat_Production_GUM_${monthName}_${year}${bureau ? `_${bureau}` : '_National'}`;
        finalizeEtatPdf(doc);
        doc.save(`${fileName}.pdf`);
        return true;
    } catch (error) {
        console.error('Error exporting to PDF:', error);
        return false;
    }
};

/**
 * Export Emissions Nettes to PDF
 */
export const exportEmissionsNettesToPDF = async (emissions, categorie, exercice) => {
    try {
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const formattedNow = new Date().toLocaleString('fr-FR', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
        const [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]);

        const N = (v) => displayNum(v) || '0';
        const MONTH_KEYS = ['janvier', 'fevrier', 'mars', 'avril', 'mai', 'juin', 'juillet', 'aout', 'septembre', 'octobre', 'novembre', 'decembre'];

        const body = emissions.map((e) => ([
            e.compagnie?.raisonSociale?.toUpperCase() || e.compagnie?.code || '',
            ...MONTH_KEYS.map((k) => N(e[k])),
            N(e.total),
        ]));
        body.push([
            'TOTAL',
            ...MONTH_KEYS.map((k) => N(emissions.reduce((s, e) => s + (Number(e[k]) || 0), 0))),
            N(emissions.reduce((s, e) => s + (Number(e.total) || 0), 0)),
        ]);
        const lastRow = body.length - 1;

        autoTable(doc, {
            head: [['COMPAGNIE', 'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc', 'TOTAL']],
            body,
            theme: 'grid',
            startY: 32,
            styles: {
                fontSize: 6,
                cellPadding: { top: 0.9, bottom: 0.9, left: 1.2, right: 1.2 },
                lineWidth: 0.1, lineColor: [215, 215, 225], textColor: [45, 45, 45],
                halign: 'right', valign: 'middle', overflow: 'linebreak',
            },
            headStyles: { fillColor: NAVY, textColor: 255, fontSize: 6, fontStyle: 'bold', halign: 'center', lineWidth: 0.1, lineColor: NAVY },
            alternateRowStyles: { fillColor: [246, 247, 251] },
            margin: { top: 32, bottom: 22, left: 12, right: 12 },
            columnStyles: {
                0: { cellWidth: 40, halign: 'left' },
                13: { cellWidth: 22 },
            },
            didParseCell: (d) => {
                if (d.row.index === lastRow || d.column.index === 13) {
                    d.cell.styles.fontStyle = 'bold';
                }
                if (d.row.index === lastRow) {
                    d.cell.styles.fillColor = [225, 227, 240];
                    d.cell.styles.textColor = NAVY;
                }
            },
            didDrawPage: () => drawEtatHeaderFooter(doc, {
                title: 'ÉTAT DES ÉMISSIONS NETTES CÉDÉES',
                subtitle: `Catégorie : ${categorie} — Exercice : ${exercice}`,
                enteteData, logos, formattedNow,
            }),
        });

        const fileName = `Emissions_Nettes_${categorie}_${exercice}`;
        finalizeEtatPdf(doc);
        doc.save(`${fileName}.pdf`);
        return true;
    } catch (error) {
        console.error('Error exporting to PDF:', error);
        return false;
    }
};

// ════════════════════════════════════════════════════════════════════════
// Rapport mensuel VT — détail journalier des opérations de production
// ════════════════════════════════════════════════════════════════════════
const VT_GROUPS = [
    { label: 'VOITURE', base: 12000, prix: 13000, nb: 'nbVoiture', mt: 'mtVoiture' },
    { label: 'CAMION', base: 18500, prix: 19500, nb: 'nbCamion', mt: 'mtCamion' },
    { label: 'TOTAL', base: null, prix: null, nb: 'nbTotalAn', mt: 'mtTotalAn' },
    { label: 'PROROGATION VL 7J', base: 6735, prix: 7735, nb: 'nbProrogVl7', mt: 'mtProrogVl7' },
    { label: 'PROROGATION VL 15J', base: 12000, prix: 13000, nb: 'nbProrogVl15', mt: 'mtProrogVl15' },
    { label: 'PROROGATION CAMION 7J', base: 9765, prix: 10765, nb: 'nbProrogCam7', mt: 'mtProrogCam7' },
    { label: 'PROROGATION CAMION 15J', base: 18500, prix: 19500, nb: 'nbProrogCam15', mt: 'mtProrogCam15' },
    { label: 'CP VL PLAQUÉS', base: 5000, prix: 6000, nb: 'nbCpPlaque', mt: 'mtCpPlaque' },
    { label: 'CP VL NON PLAQUÉS', base: 0, prix: 3000, nb: 'nbCpNonPlaque', mt: 'mtCpNonPlaque' },
    { label: 'RENOUVEL VL', base: 12000, prix: 13000, nb: 'nbRenouvelVl', mt: 'mtRenouvelVl' },
    { label: 'RENOUVEL CAMION', base: 18500, prix: 19500, nb: 'nbRenouvelCam', mt: 'mtRenouvelCam' },
    { label: 'DUPLICATA', base: 0, prix: 1500, nb: 'nbDuplicata', mt: 'mtDuplicata' },
];
const VT_SINGLES = [
    { label: 'TOTAL ENCAISSÉE', key: 'totalEncaisse' },
    { label: 'Base commission STAC', key: 'baseCommissionStac' },
    { label: 'REPORT VEILLE', key: 'reportVeille' },
    { label: 'VERSEMENT DU JOUR (hors timbre)', key: 'versementDuJour' },
    { label: 'TIMBRE', key: 'timbre' },
    { label: 'SOLDE À REVERSER', key: 'soldeAReverser' },
];

const vtTotals = (data) => {
    const t = {};
    VT_GROUPS.forEach((g) => {
        t[g.nb] = data.reduce((s, r) => s + (Number(r[g.nb]) || 0), 0);
        t[g.mt] = data.reduce((s, r) => s + (Number(r[g.mt]) || 0), 0);
    });
    VT_SINGLES.forEach((c) => { t[c.key] = data.reduce((s, r) => s + (Number(r[c.key]) || 0), 0); });
    return t;
};

/** Export Excel du rapport mensuel VT (en-têtes groupés + ligne TOTAL). */
export const exportVTProductionToExcel = (data, mois) => {
    try {
        const monthName = frenchMonth(mois.split('-')[1]);
        const year = mois.split('-')[0];

        // En-têtes : ligne 1 = groupes (PU/Base) ; ligne 2 = NOMBRE/MONTANT.
        const h1 = ['DATE'];
        const h2 = [''];
        VT_GROUPS.forEach((g) => {
            const suffix = g.prix != null ? ` (PU ${g.prix} / Base ${g.base})` : '';
            h1.push(`${g.label}${suffix}`, '');
            h2.push('NOMBRE', 'MONTANT');
        });
        VT_SINGLES.forEach((s) => { h1.push(s.label); h2.push(''); });

        const rows = data.map((r) => {
            const row = [frenchDateOnly(r.date)];
            VT_GROUPS.forEach((g) => { row.push(Number(r[g.nb]) || 0, Number(r[g.mt]) || 0); });
            VT_SINGLES.forEach((s) => { row.push(Number(r[s.key]) || 0); });
            return row;
        });

        const t = vtTotals(data);
        const totalRow = ['TOTAL'];
        VT_GROUPS.forEach((g) => { totalRow.push(t[g.nb], t[g.mt]); });
        VT_SINGLES.forEach((s) => { totalRow.push(t[s.key]); });

        const aoa = [h1, h2, ...rows, totalRow];
        const ws = XLSX.utils.aoa_to_sheet(aoa);

        // Fusions : libellés de groupes (2 colonnes), DATE et colonnes simples (2 lignes).
        const merges = [{ s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }]; // DATE
        let c = 1;
        VT_GROUPS.forEach(() => { merges.push({ s: { r: 0, c }, e: { r: 0, c: c + 1 } }); c += 2; });
        VT_SINGLES.forEach(() => { merges.push({ s: { r: 0, c }, e: { r: 1, c } }); c += 1; });
        ws['!merges'] = merges;
        ws['!cols'] = aoa[0].map((_, i) => ({ wch: i === 0 ? 12 : 12 }));

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Production VT');
        XLSX.writeFile(wb, `Etat_Production_VT_${monthName}_${year}.xlsx`);
        return true;
    } catch (error) {
        console.error('Error exporting VT to Excel:', error);
        return false;
    }
};

/** Export PDF du rapport mensuel VT (A3 paysage, entête Pool + footer logos). */
export const exportVTProductionToPDF = async (data, mois) => {
    try {
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
        const formattedNow = new Date().toLocaleString('fr-FR', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
        const [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]);
        const monthName = frenchMonth(mois.split('-')[1]);
        const year = mois.split('-')[0];
        const N = (v) => displayNum(v) || '0';

        // En-tête sur 2 lignes : groupes (colSpan 2) + colonnes simples (rowSpan 2).
        const head1 = [{ content: 'DATE', rowSpan: 2 }];
        const head2 = [];
        VT_GROUPS.forEach((g) => {
            const sub = g.prix != null ? `\n${g.prix}` : '';
            head1.push({ content: g.label + sub, colSpan: 2, styles: { halign: 'center' } });
            head2.push({ content: 'Nb', styles: { halign: 'center' } }, { content: 'Montant', styles: { halign: 'center' } });
        });
        VT_SINGLES.forEach((s) => { head1.push({ content: s.label, rowSpan: 2, styles: { halign: 'center' } }); });

        const body = data.map((r) => {
            const row = [frenchDateOnly(r.date)];
            VT_GROUPS.forEach((g) => { row.push(`${Number(r[g.nb]) || 0}`, N(r[g.mt])); });
            VT_SINGLES.forEach((s) => { row.push(N(r[s.key])); });
            return row;
        });
        const t = vtTotals(data);
        const totalRow = ['TOTAL'];
        VT_GROUPS.forEach((g) => { totalRow.push(`${t[g.nb]}`, N(t[g.mt])); });
        VT_SINGLES.forEach((s) => { totalRow.push(N(t[s.key])); });
        body.push(totalRow);
        const lastRow = body.length - 1;

        autoTable(doc, {
            head: [head1, head2],
            body,
            theme: 'grid',
            startY: 32,
            styles: { fontSize: 5.5, cellPadding: 0.6, lineWidth: 0.1, lineColor: [200, 200, 210], textColor: [40, 40, 40], halign: 'right', overflow: 'linebreak', valign: 'middle' },
            headStyles: { fillColor: NAVY, textColor: 255, fontSize: 5.2, fontStyle: 'bold', halign: 'center', lineWidth: 0.1, lineColor: NAVY },
            alternateRowStyles: { fillColor: [246, 247, 251] },
            columnStyles: { 0: { halign: 'center' } },
            margin: { top: 32, bottom: 18, left: 8, right: 8 },
            didParseCell: (d) => {
                if (d.row.index === lastRow && d.section === 'body') {
                    d.cell.styles.fontStyle = 'bold';
                    d.cell.styles.fillColor = [225, 227, 240];
                    d.cell.styles.textColor = NAVY;
                }
            },
            didDrawPage: () => drawEtatHeaderFooter(doc, {
                title: `DÉTAIL JOURNALIER DES OPÉRATIONS DE PRODUCTION VT — ${monthName.toUpperCase()} ${year}`,
                subtitle: '',
                enteteData, logos, formattedNow,
            }),
        });

        finalizeEtatPdf(doc);
        doc.save(`Etat_Production_VT_${monthName}_${year}.pdf`);
        return true;
    } catch (error) {
        console.error('Error exporting VT to PDF:', error);
        return false;
    }
};